import { motion } from 'framer-motion';
import React from 'react';
import {
  FiCheckCircle,
  FiCloud,
  FiCode,
  FiCpu,
  FiDivideSquare,
  FiEye,
  FiGitBranch,
  FiLayers,
  FiLock,
  FiMessageSquare,
  FiServer,
  FiShield,
  FiTarget,
  FiTrendingUp,
  FiUsers,
  FiZap
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import NewsletterSection from '../components/NewsletterSection';

const Services = () => {
  const servicesOverview = [
    {
      id: 'IT Staffing & Team Augmentation',
      title: 'IT Staffing & Team Augmentation',
      subtitle: 'Expert Talent Acquisition',
      description: 'Access top-tier engineers, analysts, and technologists for short-term projects or long-term growth.',
      icon: FiUsers,
      gradient: 'from-blue-500 to-indigo-600',
      bgGradient: 'from-blue-50 to-indigo-50',
      stats: { projects: '50+', placements: '200+', satisfaction: '98%' }
    },
    {
      id: 'Digital Transformation Consulting',
      title: 'Project Services',
      subtitle: 'End-to-End Solutions',
      description: 'Align your people, processes, and technologies to stay agile and future-ready.',
      icon: FiTarget,
      gradient: 'from-purple-500 to-blue-600',
      bgGradient: 'from-purple-50 to-blue-50',
      stats: { projects: '30+', onTime: '95%', budget: '100%' }
    },
    {
      id: 'cloud-infrastructure',
      title: 'Cloud & Infrastructure Solutions',
      subtitle: 'Scalable Solutions',
      description: 'Migrate, modernize, and manage your tech stack with secure and scalable cloud architecture.',
      icon: FiCloud,
      gradient: 'from-cyan-500 to-blue-600',
      bgGradient: 'from-cyan-50 to-blue-50',
      stats: { migrations: '25+', uptime: '99.9%', savings: '40%' }
    },
    {
      id: 'ai-services',
      title: 'AI & Intelligent Development',
      subtitle: 'Future Technology',
      description: 'Automate decisions, personalize interactions, and solve business problems with smart, scalable AI systems.',
      icon: FiCpu,
      gradient: 'from-purple-500 to-pink-600',
      bgGradient: 'from-purple-50 to-pink-50',
      stats: { models: '15+', accuracy: '95%', automation: '60%' }
    },
    {
      id: 'cybersecurity',
      title: 'Cybersecurity Solutions',
      subtitle: 'Advanced Protection',
      description: 'Comprehensive security solutions to protect your digital assets.',
      icon: FiShield,
      gradient: 'from-red-500 to-orange-600',
      bgGradient: 'from-red-50 to-orange-50',
      stats: { threats: '99.8%', incidents: '0', compliance: '100%' }
    },
    {
      id: 'Custom Software Development',
      title: 'Custom Software Development',
      subtitle: 'Tailored Solutions',
      description: 'Build tailored apps and platforms—from MVPs to full-scale enterprise systems—with clean code and fast deployment.',
      icon: FiCode,
      gradient: 'from-green-500 to-lime-600',
      bgGradient: 'from-green-50 to-lime-50',
      stats: { projects: '10+', custom: '100%', efficiency: '90%' }
    },
    {
      id: 'Data Engineering & Analytics',
      title: 'Data Engineering & Analytics',
      subtitle: 'Data-Driven Insights',
      description: 'Turn raw data into insight with data pipelines, dashboards, and predictive modeling.',
      icon: FiCpu,
      gradient: 'from-purple-500 to-blue-600',
      bgGradient: 'from-red-50 to-skyblue-50',
      stats: { projects: '12+', custom: '90%', efficiency: '96%' }
    },
    {
      id: 'Product Strategy & MVP Design',
      title: 'Product Strategy & MVP Design',
      subtitle: 'Future-Ready Solutions',
      description: 'Validate your ideas and bring products to life quickly with lean, iterative prototyping and launch planning.',
      icon: FiDivideSquare,
      gradient: 'from-green-500 to-blue-600',
      bgGradient: 'from-green-50 to-blue-50',
      stats: { projects: '10+', custom: '90%', efficiency: '96%' }
    },
  ];

  const itStaffingServices = [
    { title: 'Contract Staffing', desc: 'Flexible contract professionals for project-based needs', icon: FiUsers },
    { title: 'Permanent Placement', desc: 'Full-time talent acquisition for long-term growth', icon: FiTarget },
    { title: 'Contract-to-Hire', desc: 'Evaluate talent before making permanent commitments', icon: FiCheckCircle },
    { title: 'Managed Teams', desc: 'Complete team management and project delivery', icon: FiLayers }
  ];

  const projectServices = [
    { title: 'Strategic Planning', desc: 'Comprehensive project roadmaps aligned with business objectives', icon: FiTarget },
    { title: 'Resource Management', desc: 'Optimal allocation of talent and technology resources', icon: FiLayers },
    { title: 'Agile Execution', desc: 'Rapid delivery with iterative development methodologies', icon: FiZap },
    { title: 'Quality Assurance', desc: 'Rigorous testing and validation throughout the lifecycle', icon: FiCheckCircle }
  ];

  const cloudServices = [
    { title: 'Cloud Migration', desc: 'Seamless transition to cloud infrastructure', icon: FiCloud },
    { title: 'Infrastructure Management', desc: 'Ongoing management and optimization of cloud resources', icon: FiServer },
    { title: 'DevOps Solutions', desc: 'Streamlined development and deployment pipelines', icon: FiGitBranch },
    { title: 'Monitoring & Analytics', desc: 'Real-time insights and performance optimization', icon: FiTrendingUp }
  ];

  const aiServices = [
    { title: 'Machine Learning', desc: 'Advanced ML models for predictive analytics and automation', icon: FiCpu },
    { title: 'Conversational AI', desc: 'Intelligent chatbots and virtual assistants', icon: FiMessageSquare },
    { title: 'Computer Vision', desc: 'Image recognition and visual data processing', icon: FiEye },
    { title: 'Process Automation', desc: 'Intelligent automation for operational efficiency', icon: FiZap }
  ];

  const securityServices = [
    { title: 'Security Assessment', desc: 'Comprehensive evaluation of your security posture', icon: FiShield },
    { title: 'Threat Detection', desc: 'Advanced monitoring and threat identification', icon: FiEye },
    { title: 'Identity Management', desc: 'Secure access control and user authentication', icon: FiLock },
    { title: 'Compliance Solutions', desc: 'Regulatory compliance and audit support', icon: FiCheckCircle }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4 font-accent tracking-wide">
              Complete Solutions
            </span>
            <h1 className="text-5xl md:text-6xl font-bold leading-tight font-display tracking-tight text-gray-900 mb-6">
              Our
              <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
                Services Portfolio
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Comprehensive technology solutions designed to accelerate your digital transformation, 
              enhance operational efficiency, and drive sustainable business growth.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Overview Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              
              <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
                Overview
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Explore our comprehensive range of services designed to meet your technology and business needs.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-20">
            {servicesOverview.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className={`bg-gradient-to-br ${service.bgGradient} p-8 rounded-3xl border border-white/50 hover:shadow-2xl transition-all duration-500 group-hover:scale-105 h-full`}>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${service.gradient} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <service.icon className="text-white" size={24} />
                    </div>
                    <span className="px-3 py-1 bg-white/60 text-gray-700 rounded-full text-xs font-medium font-accent tracking-wide">
                      {service.subtitle}
                    </span>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 font-display tracking-tight">
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-700 mb-6 font-body leading-relaxed">
                    {service.description}
                  </p>

                  <div className="grid grid-cols-3 gap-4">
                    {Object.entries(service.stats).map(([key, value], idx) => (
                      <div key={idx} className="text-center">
                        <div className={`text-xl font-bold bg-gradient-to-br ${service.gradient} bg-clip-text text-transparent font-display`}>
                          {value}
                        </div>
                        <div className="text-xs text-gray-600 font-body capitalize">{key}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* IT Staffing Detailed Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              IT Staffing
              <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Solutions
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Connect with top-tier technology professionals who can accelerate your projects and drive innovation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {itStaffingServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="group p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <service.icon className="text-white" size={20} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 font-heading tracking-wide">
                  {service.title}
                </h3>
                <p className="text-gray-600 font-body leading-relaxed">
                  {service.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Services Detailed Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              Project Services
              <span className="block bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                That Deliver Results
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              End-to-end project management ensuring your initiatives are delivered on time, within budget, and exceed expectations.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {projectServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="group p-6 bg-white rounded-2xl border border-purple-100 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <service.icon className="text-white" size={20} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 font-heading tracking-wide">
                  {service.title}
                </h3>
                <p className="text-gray-600 font-body leading-relaxed">
                  {service.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cloud Infrastructure Detailed Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              Cloud Infrastructure
              <span className="block bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                Solutions
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Modern cloud solutions that enhance performance, scalability, and operational efficiency for your business.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {cloudServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="group p-6 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl border border-cyan-100 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <service.icon className="text-white" size={20} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 font-heading tracking-wide">
                  {service.title}
                </h3>
                <p className="text-gray-600 font-body leading-relaxed">
                  {service.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Services Detailed Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full filter blur-3xl animate-pulse animation-delay-1000"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-display tracking-tight">
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                AI & Intelligent
              </span>
              <br />
              <span className="text-white">Agent Development</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Harness the power of artificial intelligence to transform your business operations and unlock new possibilities.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {aiServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="group p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:bg-white/10 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <service.icon className="text-white" size={20} />
                </div>
                <h3 className="text-lg font-bold text-white mb-3 font-heading tracking-wide">
                  {service.title}
                </h3>
                <p className="text-gray-300 font-body leading-relaxed">
                  {service.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cybersecurity Detailed Section */}
      <section className="py-20 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              Cybersecurity
              <span className="block bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                Solutions
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Comprehensive security solutions to protect your digital assets and ensure business continuity.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {securityServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="group p-6 bg-white rounded-2xl border border-red-100 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <service.icon className="text-white" size={20} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 font-heading tracking-wide">
                  {service.title}
                </h3>
                <p className="text-gray-600 font-body leading-relaxed">
                  {service.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-white/5"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-display tracking-tight">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Let's discuss how our comprehensive services can accelerate your digital transformation and drive sustainable growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 font-accent tracking-wide"
                >
                  Get Started Today
                </motion.button>
              </Link>
              <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300 font-accent tracking-wide"
              >
                Schedule Consultation
              </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <NewsletterSection />
      <Footer />
    </div>
  );
};

export default Services; 